/**
 * @class that sorts an array of objects
 * @version 1.2.3
 */
class MergeSort {
  /**
   * @param {Array} data - Array of objects to sort.
   * @param {Boolean} rankHighToLow - if true the highest number is rank 1 - default true.
   * @param {Object} tiesOpts - object of options to handle tied values.
   */
  constructor({
    data,
    rankHighToLow = true,
    empty_value = "",
    tiesOpts = {}
  } = {}) {
    const self = this;
    self.currentRank = 1;
    self.rankHighToLow = rankHighToLow;
    self.empties = [];
    self.empty_value = empty_value;
    self.arrayContainsObjects(data);
    self.extendTiesOptions(tiesOpts);
    self.data = self.cloneArray(self.isArray(data));
  }

  /**
   * @param {Boolean} tiesOpts.enable - if true tied values are sorted again by the property of tiesOpts.sortBy - default false.
   * @param {Boolean} tiesOpts.denseRanking - if true tied ranks are given the same rank e.g. 1, 2, =3, =3, 5 if false the order is maintained e.g 1, 2, 3, 4, 5 - default true.
   * @param {Boolean} tiesOpts.sort  - sorts the ties using a fallback property - default true.
   * @param {String} tiesOpts.sortBy - determines the default property for tiesOpts.sort - default total.
   */
  extendTiesOptions(tiesOpts) {
    const self = this;

    self.tiesOpts = {
      enable: false,
      denseRanking: true,
      sort: true,
      sortBy: "total"
    };

    for (const prop in self.tiesOpts) {
      if (tiesOpts.hasOwnProperty(prop)) {
        self.tiesOpts[prop] = tiesOpts[prop];
      }
    }
  }

  sort(propToSort, dataType) {
    const self = this;
    const sortedData = self.mergeSort(
      self.prepData(propToSort),
      propToSort,
      dataType
    );
    return self.tiesOpts.enable
      ? self.updateRanksWithTies(sortedData, propToSort, dataType)
      : self.updateRanksWithoutTies(sortedData);
  }

  /**
   * returns sorted array of objects
   *
   * @param {Object} update_ranks - settings for updating ranks when sorting.
   * @param {String} update_ranks.propToSort - Property to sort by.
   * @param {String} update_ranks.dataType - Specifies datatype to help with formatting the values.
   * @param {Object} static_ranks - settings for not ranks when sorting.
   * @param {String} static_ranks.propToSort - Property to sort by.
   * @param {String} static_ranks.dataType - Specifies datatype to help with formatting the values.
   *
   * if only update_ranks.propToSort is passed then it only updates the rank.
   * if both update_ranks.propToSort && static_ranks.propToSort is passed then it updates the rank first then sorts without updating the rank.
   * if only static_ranks.propToSort is passed the it doesnt update the rank and just sorts.
   */
  lowToHigh({
    update_ranks = { propToSort: null, dataType: "number" },
    static_ranks = { propToSort: null, dataType: "number" }
  } = {}) {
    const self = this;
    self.sortHighToLow = false;

    self.validateObjectProps([
      update_ranks.propToSort,
      static_ranks.propToSort
    ]);

    return !update_ranks.propToSort && static_ranks.propToSort
      ? self.mergeSort(
          self.prepData(static_ranks.propToSort),
          static_ranks.propToSort,
          static_ranks.dataType
        )
      : update_ranks.propToSort && !static_ranks.propToSort
      ? self.sort(update_ranks.propToSort, update_ranks.dataType)
      : update_ranks.propToSort && static_ranks.propToSort
      ? self.mergeSort(
          self.sort(update_ranks.propToSort, update_ranks.dataType),
          static_ranks.propToSort,
          static_ranks.dataType
        )
      : false;
  }

  /**
   * returns sorted array of objects
   *
   * @param {Object} update_ranks - settings for updating ranks when sorting.
   * @param {String} update_ranks.propToSort - Property to sort by.
   * @param {String} update_ranks.dataType - Specifies datatype to help with formatting the values.
   * @param {Object} static_ranks - settings for not ranks when sorting.
   * @param {String} static_ranks.propToSort - Property to sort by.
   * @param {String} static_ranks.dataType - Specifies datatype to help with formatting the values.
   *
   * if only update_ranks.propToSort is passed then it only updates the rank.
   * if both update_ranks.propToSort && static_ranks.propToSort is passed then it updates the rank first then sorts without updating the rank.
   * if only static_ranks.propToSort is passed the it doesnt update the rank and just sorts.
   */
  highToLow({
    update_ranks = { propToSort: null, dataType: "number" },
    static_ranks = { propToSort: null, dataType: "number" }
  } = {}) {
    const self = this;
    self.sortHighToLow = true;

    self.validateObjectProps([
      update_ranks.propToSort,
      static_ranks.propToSort
    ]);

    return !update_ranks.propToSort && static_ranks.propToSort
      ? self.mergeSort(
          self.prepData(static_ranks.propToSort),
          static_ranks.propToSort,
          static_ranks.dataType
        )
      : update_ranks.propToSort && !static_ranks.propToSort
      ? self.sort(update_ranks.propToSort, update_ranks.dataType)
      : update_ranks.propToSort && static_ranks.propToSort
      ? self.mergeSort(
          self.sort(update_ranks.propToSort, update_ranks.dataType),
          static_ranks.propToSort,
          static_ranks.dataType
        )
      : false;
  }

  updateRanksWithTies(sortedData, propToSort, dataType) {
    const self = this;
    self.ties = [];
    self.finalSort = [];
    self.currentRank = 1;

    sortedData = this.prepareArrayOrder(sortedData);

    let i;
    for (i = 0; i < sortedData.length; i++) {
      const current = sortedData[i];
      const next = sortedData[i + 1];
      const previous = sortedData[i - 1];
      const currentValue = self.formatValue(current[propToSort], dataType);
      const previousValue = previous
        ? self.formatValue(previous[propToSort], dataType)
        : false;
      const nextValue = next
        ? self.formatValue(next[propToSort], dataType)
        : false;

      if (previous && currentValue === previousValue) {
        self.ties.push(current);
      } else if (next && currentValue === nextValue) {
        if (previous && currentValue !== previousValue) {
          self.checkForTies(propToSort, dataType);
        }
        self.ties.push(current);
      } else {
        self.checkForTies(propToSort, dataType);
        current.rank = self.tiesOpts.denseRanking ? self.currentRank : i + 1;
        self.finalSort.push(current);
        self.currentRank = self.tiesOpts.denseRanking
          ? self.currentRank + 1
          : current.rank + 1;
      }
    }

    self.checkForTies(propToSort, dataType, true);
    self.data = self.cloneArray(self.finalSort);
    return self.prepareArrayOrder(self.finalSort);
  }

  prepareArrayOrder(data) {
    const self = this;
    return (self.rankHighToLow && !self.sortHighToLow) ||
      (!self.rankHighToLow && self.sortHighToLow)
      ? data.reverse()
      : data;
  }

  checkForTies(propToSort, dataType, check_empties) {
    // if there are ties update rank, add to final sort and empty
    const self = this;
    if (self.ties.length) {
      self.finalSort = self.tiesOpts.sort
        ? [...self.finalSort, ...self.sortTies(propToSort, dataType)]
        : [...self.finalSort, ...self.ties];
      self.ties = [];

      self.currentRank = self.tiesOpts.denseRanking
        ? self.currentRank + 1
        : self.currentRank;
    }

    if (check_empties) {
      self.addEmpties(self.ties);
      self.checkForTies(propToSort, dataType);
    }
  }

  addEmpties(array, push = true) {
    const self = this;

    if (self.empties.length) {
      self.empties.forEach(obj => {
        push ? array.push(obj) : array.unshift(obj);
      });
    }

    return array;
  }

  addRankToTies() {
    const self = this;

    for (const tie of self.ties) {
      tie.rank =
        self.tiesOpts && self.tiesOpts.denseRanking
          ? `=${self.currentRank}`
          : self.currentRank;

      if (!self.tiesOpts.denseRanking) {
        self.currentRank++;
      }
    }
    return self.ties;
  }

  sortTies(propToSort, dataType) {
    const self = this;

    if (propToSort !== self.tiesOpts.sortBy) {
      self.ties = [
        ...self.mergeSort(self.ties, self.tiesOpts.sortBy, dataType)
      ];
    }

    self.ties =
      (!self.rankHighToLow && self.sortHighToLow) ||
      (self.rankHighToLow && !self.sortHighToLow)
        ? self.ties.reverse()
        : self.ties;

    return self.addRankToTies();
  }

  updateRanksWithoutTies(sortedData) {
    const self = this;
    const ranking_condition =
      (self.rankHighToLow && self.sortHighToLow) ||
      (!self.rankHighToLow && !self.sortHighToLow);

    sortedData = self.addEmpties(sortedData, ranking_condition);

    sortedData.forEach((dataset, i) => {
      dataset.rank = ranking_condition
        ? (self.currentRank = i + 1)
        : (self.currentRank = sortedData.length + 1 - (i + 1));
    });

    self.addEmpties(self.data);

    return sortedData;
  }

  merge(left, right, propToSort, dataType) {
    const self = this;
    const results = [];

    while (left.length && right.length) {
      const leftValue = self.formatValue(left[0][propToSort], dataType);
      const rightValue = self.formatValue(right[0][propToSort], dataType);
      const condition = self.sortHighToLow
        ? leftValue >= rightValue
        : leftValue < rightValue;

      if (condition) {
        results.push(left.shift());
      } else {
        results.push(right.shift());
      }
    }
    return [...results, ...left, ...right];
  }

  mergeSort(arr, propToSort, dataType) {
    if (arr.length < 2) {
      return arr;
    }

    const self = this;
    const center = Math.floor(arr.length / 2);
    const left = arr.slice(0, center);
    const right = arr.slice(center);

    return self.merge(
      self.mergeSort(left, propToSort, dataType),
      self.mergeSort(right, propToSort, dataType),
      propToSort,
      dataType
    );
  }

  /**
   * @param {String} propToCheckFor - The object property to check exists in object.
   */
  checkForProp(propToCheckFor) {
    let propCounter;

    propCounter = 0;

    this.data.forEach(obj => {
      const values = Object.keys(obj).includes(propToCheckFor)
        ? propCounter++
        : false;
    });

    return propCounter ? propCounter : false;
  }

  /**
   * @param {Number/Date/String} value - value to sort.
   * @param {String} dataType - specifies the data type. date, string. Defaults to number.
   */
  formatValue(value, dataType = "number") {
    if (value !== undefined) {
      value =
        dataType === "date"
          ? new Date(value).valueOf()
          : dataType === "number"
          ? value
          : dataType === "string"
          ? value.toLowerCase()
          : false;
    }
    return value;
  }

  /**
   * @param {Array} array - Array of objects to clone.
   */
  cloneArray(array) {
    const freshArray = array.map(obj => {
      return Object.assign({}, obj);
    });
    return freshArray;
  }

  update(data) {
    const self = this;
    self.data = self.isArray(data);
    self.arrayContainsObjects(data);
    return self;
  }

  prepData(propToSort) {
    const self = this;

    self.remove_empties(propToSort);

    return self.data;
  }

  remove_empties(propToSort) {
    const self = this;
    self.empties = [];

    for (let i = 0; i < self.data.length; i++) {
      if (self.data[i][propToSort] === this.empty_value) {
        this.empties.push(self.data[i]);
        this.data.splice(i, 1);
        i = i - 1;
      }
    }
  }

  getData() {
    return this.data;
  }

  isArray(data) {
    return Array.isArray(data)
      ? data
      : this.throwError("Data must be an array of objects");
  }

  /**
   * @param {object} obj - expect to be an object passed from the array index.
   */
  isObject(obj) {
    const keys = Object.keys(obj);

    return (
      obj &&
      typeof obj === "object" &&
      obj.constructor === Object &&
      !!keys.length
    );
  }

  validateObjectProps(props) {
    const self = this;
    props.forEach(prop => {
      if (prop) {
        self.validateObjectProp(prop);
      }
    });
  }

  validateObjectProp(propToCheck) {
    const self = this;

    if (!propToCheck) {
      self.throwError("propToSort must be passed as an argument");
    } else if (!self.checkForProp(propToCheck)) {
      self.throwError(
        `objects in the data array do not have ${propToCheck} as a property`
      );
    } else if (self.checkForProp(propToCheck) !== self.data.length) {
      self.throwError(`Not all properties to sort are ${propToCheck}`);
    }
  }

  arrayContainsObjects(array) {
    const self = this;

    self.checkArrayLength(array);

    const allObjects = array.map(obj => {
      return self.isObject(obj);
    });
    return allObjects.includes(false)
      ? self.throwError(
          "data can only contain objects or object can't be empty"
        )
      : true;
  }

  checkArrayLength(array) {
    if (!array.length) {
      this.throwError("No array item/s to sort");
    }
  }

  /**
   * @param {String} message - Message to throw when theres an error.
   */
  throwError(message) {
    throw new Error(message);
  }
}

// ************************************</editor-fold>
// EXPORT
// ************************************<editor-fold>
// export { MergeSort };
